import { Component, computed, inject, signal, OnInit, runInInjectionContext, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Auth, confirmPasswordReset, verifyPasswordResetCode } from '@angular/fire/auth';
import { AuthService } from '../service/auth';
import { EyeIconComponent } from '../../shared/svg/eye-icon';
import { EyeSlashIconComponent } from '../../shared/svg/eye-slash-icon';

@Component({
	selector: 'app-reset-password',
	imports: [CommonModule, FormsModule, EyeIconComponent, EyeSlashIconComponent],
	templateUrl: './reset-password.html',
	styleUrl: './reset-password.scss'
})
export class ResetPasswordComponent implements OnInit {
	private auth = inject(Auth);
	private authService = inject(AuthService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private injector = inject(Injector);

	// Form data
	newPassword = '';
	confirmPassword = '';

	// UI state
	isLoading = signal<boolean>(false);
	isValidatingCode = signal<boolean>(true);
	errorMessage = signal<string>('');
	successMessage = signal<string>('');
	showPassword = signal<boolean>(false);
	showConfirmPassword = signal<boolean>(false);

	// Code validation
	isCodeValid = signal<boolean>(false);
	email = signal<string>('');

	// Signal to track if avatar is clicked (showing surprised state)
	private isSurprised = signal(false);

	// Computed property to determine which avatar image to display
	avatarImage = computed(() => {
		// Show surprised image when clicked
		if (this.isSurprised()) {
			return 'assets/images/avatar/Crustopher-Surprised.png';
		}
		return 'assets/images/avatar/Crustopher-Happy.png';
	});

	ngOnInit(): void {
		this.handleResetPassword().catch(error => {
			console.error('Error in handleResetPassword:', error);
		});
	}

	/**
	 * Handle password reset following Firebase documentation pattern
	 */
	private async handleResetPassword(): Promise<void> {
		// Get parameters from URL as per Firebase docs
		const actionCode = this.route.snapshot.queryParams['oobCode'] || 
						   this.route.snapshot.queryParams['actionCode'];
		const continueUrl = this.route.snapshot.queryParams['continueUrl'];
		const lang = this.route.snapshot.queryParams['lang'];
		
		if (!actionCode) {
			this.errorMessage.set('Invalid or missing reset code. Please request a new password reset.');
			this.isValidatingCode.set(false);
			return;
		}

		try {
			// Verify the password reset code is valid (following Firebase docs)
			// Use runInInjectionContext to ensure Firebase API is called within injection context
			const email = await runInInjectionContext(this.injector, () => {
				return verifyPasswordResetCode(this.auth, actionCode);
			});
			this.email.set(email);
			this.isCodeValid.set(true);
		} catch (error: any) {
			console.error('Password reset code validation error:', error);
			this.errorMessage.set(this.authService.handleAuthError(error));
		} finally {
			this.isValidatingCode.set(false);
		}
	}

	/**
	 * Handle form submission for password reset
	 */
	async onSubmit(): Promise<void> {
		// Reset messages
		this.errorMessage.set('');
		this.successMessage.set('');

		// Validate form
		if (!this.validateForm()) {
			return;
		}

		this.isLoading.set(true);

		try {
			// Get the action code from URL parameters (following Firebase docs)
			const actionCode = this.route.snapshot.queryParams['oobCode'] || 
							   this.route.snapshot.queryParams['actionCode'];
			
			// Save the new password (following Firebase docs pattern)
			// Use runInInjectionContext to ensure Firebase API is called within injection context
			await runInInjectionContext(this.injector, () => {
				return confirmPasswordReset(this.auth, actionCode, this.newPassword);
			});
			this.successMessage.set('Password reset successfully! You can now sign in with your new password.');
			
			// Redirect to login after 3 seconds
			setTimeout(() => {
				this.router.navigate(['/auth/login']);
			}, 3000);
		} catch (error: any) {
			console.error('Password reset error:', error);
			this.errorMessage.set(this.authService.handleAuthError(error));
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Validate form inputs
	 */
	private validateForm(): boolean {
		if (!this.newPassword) {
			this.errorMessage.set('Please enter a new password.');
			return false;
		}

		if (this.newPassword.length < 6) {
			this.errorMessage.set('Password must be at least 6 characters long.');
			return false;
		}

		if (this.newPassword !== this.confirmPassword) {
			this.errorMessage.set('Passwords do not match.');
			return false;
		}

		return true;
	}

	/**
	 * Toggle password visibility
	 */
	togglePasswordVisibility(): void {
		this.showPassword.set(!this.showPassword());
	}

	/**
	 * Toggle confirm password visibility
	 */
	toggleConfirmPasswordVisibility(): void {
		this.showConfirmPassword.set(!this.showConfirmPassword());
	}

	/**
	 * Navigate to login page
	 */
	goToLogin(): void {
		this.router.navigate(['/auth/login']);
	}

	/**
	 * Request new password reset
	 */
	goToForgotPassword(): void {
		this.router.navigate(['/auth/forgot-password']);
	}

	// Handle avatar click to show surprised state
	onAvatarClick() {
		this.isSurprised.set(true);
		// Revert back to happy state after 500ms
		setTimeout(() => {
			this.isSurprised.set(false);
		}, 500);
	}
}
